import React, { useEffect, useMemo, useState } from "react"
import { IBookingTariffSegment, ICarriageLayout, ICarriageSeat, ISeat } from "../Models"
import { Button, ListGroup, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { selectSeat } from "../Actions";
import SvgChevronDoubleDown from "../../Shared/Icons/bootstrap/ChevronDoubleDown";
import SvgChevronDoubleUp from "../../Shared/Icons/bootstrap/ChevronDoubleUp";
import SvgChevronDoubleLeft from "../../Shared/Icons/bootstrap/ChevronDoubleLeft";
import SvgChevronDoubleRight from "../../Shared/Icons/bootstrap/ChevronDoubleRight";

export const Carriage = (props: {carriage: ICarriageLayout, segment: IBookingTariffSegment, selectedSeats: { itemId: string, seat: ISeat }[]}) => {

	const dispatch = useDispatch();
	const carriage = props.carriage;

	const [activeSeat, setActiveSeat] = useState<ICarriageSeat>();


	const selectedSeats = useMemo(() => {
		if (!props.carriage || !props.selectedSeats)
			return [];

		return props.selectedSeats.
			map(x => ({ itemId: x.itemId, seat: props.carriage.seats.find(y => y.seatNumber === x.seat?.number && x.seat.carriage === props.carriage.carriageNumber) })).
			filter(x => !!x.seat);

	}, [props.carriage, props.selectedSeats]);

	//Alla som är tillgängliga i vagnen från början + våra ursprungliga valda platser - våra nuvarande valda platser
	const availableSeats = useMemo(() => {
		if (!props.carriage || !props.segment)
			return [];

		const seats = props.carriage.seats.filter(x => x.available && x.inventoryClass === props.segment.requiredProducts[0].inventoryClass);
		const originalSeats = props.carriage.seats.filter(x => props.segment.requiredProducts.some(y => y.seat?.carriage == props.carriage.carriageNumber && y.seat?.number === x.seatNumber));

		return seats.concat(originalSeats).filter(x => !props.selectedSeats.some(y => y.seat.carriage === props.carriage?.carriageNumber && y.seat?.number === x.seatNumber));
	}, [props.carriage, props.segment, props.selectedSeats]);

	//Scroll så att vald plats blir synlig
	useEffect(() => {
		if (selectedSeats?.length > 0) {
			const seatPos = selectedSeats[0].seat.yPos;
			const scrollToX = seatPos > 500 ? seatPos - 400 : 0;
			const scrollToY = seatPos > 200 ? seatPos - 150 : 0;
			// console.debug("seatPos, scrollToX, scrollToY", seatPos, scrollToX, scrollToY)
			document.querySelector(".carriage-outer").scrollTo(scrollToX, scrollToY);
		}
	}, [props.carriage]);

	const scrollDown = () => {
		document.querySelector(".carriage-outer").scrollBy(0, 200);
	};
	const scrollUp = () => {
		document.querySelector(".carriage-outer").scrollBy(0, -200);
	};
	const scrollLeft = () => {
		document.querySelector(".carriage-outer").scrollBy(-250, 0);
	};
	const scrollRight = () => {
		document.querySelector(".carriage-outer").scrollBy(250, 0);
	};

	const seatClicked = (seat: ICarriageSeat) => {
		if (props.segment.requiredProducts.length === 1)
			dispatch(selectSeat(props.segment.requiredProducts[0].itemId, { carriage: props.carriage.carriageNumber, number: seat.seatNumber }));
		else
			setActiveSeat(seat);
	};

	const itemSelected = (itemId: string) => {
		dispatch(selectSeat(itemId, { carriage: props.carriage.carriageNumber, number: activeSeat.seatNumber }));
		setActiveSeat(null);
	}

	const moveAll = () => {
		console.debug("---- Flytta alla resenärer ----")
		const items = props.segment.requiredProducts;
		//Flytta den första resenären
		console.debug("Flyttar resenär", items[0].itemId, activeSeat);
		dispatch(selectSeat(items[0].itemId, { carriage: props.carriage.carriageNumber, number: activeSeat.seatNumber }));
		let lastSeat = activeSeat;
		let seats = availableSeats.filter(x => x.seatNumber != activeSeat.seatNumber); //Nu är den inte längre ledig
		//Flytta resten så nära som möjligt
		for (let i=1; i<items.length; i++) {
			let seat = seats.find(x => x.rowPosition === lastSeat.rowPosition && x.rowNumber == lastSeat.rowNumber) || 		 //Finns det en med samma position och row? (= mittemot i kupé)
					seats.find(x => x.rowNumber == lastSeat.rowNumber && (x.rowPosition === lastSeat.rowPosition+1 || x.rowPosition === lastSeat.rowPosition-1)) || //Annars, finns det en med positionen före eller efter på samma rad?
					seats.find(x => x.rowPosition == lastSeat.rowPosition && (x.rowNumber === lastSeat.rowNumber+1 || x.rowNumber === lastSeat.rowNumber-1)); //Annars, finns det en med samma position på raden efter eller före?

			if (!seat) //Om vi inte hittade någon plats försök bara ta nästa lediga plats (eller föregående)
				seat = seats.find(x => parseInt(x.seatNumber) > parseInt(lastSeat.seatNumber)) || seats.find(x => parseInt(x.seatNumber) < parseInt(lastSeat.seatNumber));

			if (seat) {
				console.debug("Flyttar resenär", items[i].itemId, seat);
				dispatch(selectSeat(items[i].itemId, { carriage: props.carriage.carriageNumber, number: seat.seatNumber }));
				lastSeat = seat;
				seats = seats.filter(x => x.seatNumber != seat.seatNumber); //Ta bort från tillgänliga platser
			}
		}
		setActiveSeat(null);
	}

	if (!carriage || !props.segment)
		return null;

	return <>
		<style>{ `
			.seatmap {
				.carriage-outer {
					width: ${carriage.floorplanDimensions.width + 10}px;

					.carriage-inner {
						.seat {
							width: ${carriage.seatWidth}px;
							height: ${carriage.seatHeight}px;
						}
					}
				}

				@media (min-width: 992px) {
					border: none;

					.carriage-outer {
						height: ${carriage.floorplanDimensions.width + 20}px;
						width: 100%;

						.carriage-inner {
							top: -${(carriage.floorplanDimensions.height - carriage.floorplanDimensions.width)/2}px;
							left: ${(carriage.floorplanDimensions.height - carriage.floorplanDimensions.width)/2}px;
						}
					}
				}
			}
		`}</style>

		<div className="carriage-outer">
			<div className="carriage-inner" style={{ width: `${carriage.floorplanDimensions.width}`, height: `${carriage.floorplanDimensions.height}` }}>
				<img src={carriage.template} style={{ width: `${carriage.floorplanDimensions.width}`, height: `${carriage.floorplanDimensions.height}` }} />
				{ availableSeats.map(seat => <Button key={seat.seatNumber} className="seat available" onClick={() => seatClicked(seat)} title={getSeatTitle(seat)} 	style={{ top: seat.yPos, left: seat.xPos }}><span className="number">{getSeatText(seat)}</span></Button>) }
				{ selectedSeats.map((x, idx) => <Button key={x.seat.seatNumber} className="btn btn-primary seat selected" title={getSeatTitle(x.seat)} style={{ top: x.seat.yPos, left: x.seat.xPos }}><span className="number">{idx+1}</span></Button>)}
			</div>
		</div>
		<Button variant="dark" className="scrollbutton down" onClick={scrollDown}><SvgChevronDoubleDown /></Button>
		<Button variant="dark" className="scrollbutton up" onClick={scrollUp}><SvgChevronDoubleUp /></Button>
		<Button variant="dark" className="scrollbutton left" onClick={scrollLeft}><SvgChevronDoubleLeft /></Button>
		<Button variant="dark" className="scrollbutton right" onClick={scrollRight}><SvgChevronDoubleRight /></Button>

		<Modal show={!!activeSeat} size="sm" centered onHide={() => setActiveSeat(null)}>
			<Modal.Body>
				Vem vill du flytta hit?

				<ListGroup variant="flush">
					{props.segment.requiredProducts.map((x, idx) => <ListGroup.Item action key={x.itemId} onClick={() => itemSelected(x.itemId)}>Resenär {idx+1}</ListGroup.Item>)}
					{ /* TODO: Visa bara flytta alla om det finns tillräckligt många platser för hela sällskapet i vagnen */}
					<ListGroup.Item action onClick={moveAll}>Alla</ListGroup.Item>
				</ListGroup>
			</Modal.Body>
		</Modal>
	</>
}

function getSeatTitle(seat: ICarriageSeat) {
	let text = `Plats ${seat.seatNumber}`;

	if (seat.propertyCodes.includes("COMP_2P")) {
		if (seat.propertyCodes.includes("UNDER"))
			text += " (underslaf)";
		if (seat.propertyCodes.includes("MIDDLE"))
			text += " (mellanslaf)";
		if (seat.propertyCodes.includes("UPPER"))
			text += " (överslaf)";
	}

	return text;
}

function getSeatText(seat: ICarriageSeat) {
	if (seat.propertyCodes.includes("COMP_2P")) {
		if (seat.propertyCodes.includes("UNDER"))
			return "U";
		if (seat.propertyCodes.includes("MIDDLE"))
			return "M";
		if (seat.propertyCodes.includes("UPPER"))
			return "Ö";
	}

	return "";
}